import { useController, FieldValues, FieldPathByValue, UseControllerProps } from "react-hook-form";
import Autocomplete from "../Autocomplete";
import { useEffect, useState } from "react";
import { ControlledTextField as TextField } from "../TextField";
import { ALL_US_STATES_ABBRV } from "@/lib/usStateHelpers";

const stateOptions = ALL_US_STATES_ABBRV.map((value) => ({ value, label: value }));

type StateDropdownProps<F extends FieldValues, Name extends FieldPathByValue<F, string>> = UseControllerProps<
  F,
  Name
> & { required?: boolean; label: string };

type Option = { label: string; value: string };

function StateDropdown<F extends FieldValues, Name extends FieldPathByValue<F, string>>({
  control,
  name,
  rules,
  required,
  label
}: StateDropdownProps<F, Name>) {
  const {
    field: { onChange: onChangeController, value },
    fieldState: { error },
    formState: { isSubmitting }
  } = useController<F, Name>({ control, name, rules: { required: !!required, ...rules } });
  const [search, setSearch] = useState<string>(value ?? "");

  function onChange(v: Option) {
    onChangeController(v.value);
    setSearch(v.label);
  }

  function onChangeText(text: string) {
    setSearch(text);
  }

  useEffect(() => {
    setSearch(value ?? "");
  }, [value]);

  const renderItem = ({ label }: Option) => <span>{label}</span>;

  const data = search
    ? stateOptions.filter(({ label, value }) =>
        [label, value].some((option) => option.toLowerCase().includes(search.toLowerCase()))
      )
    : stateOptions;

  return (
    <Autocomplete
      data={data}
      onSelect={onChange}
      renderItem={renderItem}
      onChangeText={onChangeText}
      variant="searchableDropdown"
      query={search}
      error={!!error}
      errorText={error?.message}
      leadingIcon={null}
      label={label}
      required={required}
      disabled={isSubmitting}
    />
  );
}

type StateInputProps<F extends FieldValues, Name extends FieldPathByValue<F, string>> = Omit<
  StateDropdownProps<F, Name>,
  "label"
> & { isUSA: boolean };

export default function StateInput<F extends FieldValues, Name extends FieldPathByValue<F, string>>({
  isUSA,
  ...props
}: StateInputProps<F, Name>) {
  return isUSA ? (
    <StateDropdown label="State" {...props} />
  ) : (
    <TextField label="Province" className="w-full" {...props} />
  );
}
